import { PRINCIPALS_LOOKUP_MAX_IDS, } from '@lucidlink/dto/web-service-api';
import { Resolver } from '@lucidlink/tscore';
const DEFAULT_OPTIONS = {
    maxBatchSize: PRINCIPALS_LOOKUP_MAX_IDS,
};
/**
 * Coalesces individual principal lookups into batched requests.
 *
 * Every `resolve()` call registers its id and schedules a flush on the next
 * event-loop turn, so all lookups issued synchronously (e.g. WebFs fanning out
 * over the principals of a permission list) share one request. When the number
 * of pending ids reaches `maxBatchSize` the batch is flushed immediately.
 *
 * Ids that are not part of the response resolve to `null`. A failed request
 * rejects every lookup that was part of that batch.
 */
export class BatchedPrincipalsFetcher {
    fetchPrincipalsByIds;
    pending = new Map();
    inFlight = new Map();
    flushTimer;
    options;
    constructor(fetchPrincipalsByIds, options = {}) {
        this.fetchPrincipalsByIds = fetchPrincipalsByIds;
        this.options = { ...DEFAULT_OPTIONS, ...options };
    }
    resolve(principalId) {
        const pending = this.pending.get(principalId);
        if (pending) {
            return pending.promise;
        }
        const inFlight = this.inFlight.get(principalId);
        if (inFlight) {
            return inFlight;
        }
        const resolver = new Resolver();
        this.pending.set(principalId, resolver);
        if (this.pending.size >= this.options.maxBatchSize) {
            this.flush();
        }
        else {
            this.scheduleFlush();
        }
        return resolver.promise;
    }
    /**
     * Makes subsequent `resolve()` calls issue a new request instead of joining the
     * ones already in flight, whose results may be stale.
     */
    detachInFlight() {
        this.inFlight.clear();
    }
    /**
     * Drops the scheduled flush and rejects the lookups that were waiting for it.
     * Call it on teardown, so no request is sent with an invalidated session.
     */
    cancel() {
        clearTimeout(this.flushTimer);
        this.flushTimer = undefined;
        const batch = this.pending;
        this.pending = new Map();
        for (const resolver of batch.values()) {
            resolver.reject(new Error('Principals lookup was cancelled'));
        }
        this.inFlight.clear();
    }
    scheduleFlush() {
        if (this.flushTimer === undefined) {
            this.flushTimer = setTimeout(this.flush, 0);
        }
    }
    flush = () => {
        clearTimeout(this.flushTimer);
        this.flushTimer = undefined;
        const batch = this.pending;
        this.pending = new Map();
        if (batch.size === 0) {
            return;
        }
        const byId = this.fetchPrincipalsByIds([...batch.keys()]).then((principals) => new Map(principals.map((principal) => [
            principal.principalId,
            principal,
        ])));
        for (const [principalId, resolver] of batch) {
            const lookup = byId.then((map) => map.get(principalId) ?? null);
            this.inFlight.set(principalId, lookup);
            lookup
                .catch(() => null)
                .finally(() => {
                if (this.inFlight.get(principalId) === lookup) {
                    this.inFlight.delete(principalId);
                }
            });
            resolver.resolve(lookup);
        }
    };
}
